import { faExclamationTriangle, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import { Component } from "react";
import {
  Button,
  Col,
  Container,
  FormGroup,
  FormLabel,
  FormSelect,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip
} from "react-bootstrap";
import { injectIntl } from "react-intl";
import ModalDialogUtil from "../../../utils/ModalDialogUtil";
import ObjectUtils, { coalesce } from "../../../utils/ObjectUtils";
import TrashButton from "../../common/TrashButton";
import { cleanText } from "./../../../utils/QuestionUtils";
import { isTriggerAnswerValid } from "../../../utils/SurveyValidationUtils";

/**
 * Represents question trigger component which is used to manipulate triggers.
 */
class QuestionTriggerPopup extends Component {
  static calculateCounter(triggers) {
    let result = 0;
    triggers.forEach((trigger) => {
      result = Math.max(trigger.id, result);
    });
    return result;
  }

  constructor(props) {
    super(props);
    let { question } = this.props;
    let triggers = coalesce(ObjectUtils.getDeepProperty(question, "triggers"), []);
    this.state = {
      triggers: triggers,
      counter: QuestionTriggerPopup.calculateCounter(triggers),
      lastQuestion: null
    };
  }

  handleSetTriggers() {
    const { question, pageName, setTriggers, invisible } = this.props;

    setTriggers(pageName, question.name, this.normalizeTriggers(this.state.triggers, invisible));
    this.onCloseModal();
  }

  onCloseModal() {
    let { closeModal } = this.props;
    this.setState({ triggers: [] });
    closeModal();
    ModalDialogUtil.closeModalDialogBodyFix();
  }

  handleAddTriggerOption() {
    const { question } = this.props;
    const triggers = this.state.triggers;
    const newVal = this.state.counter + 1;
    this.setState({ counter: newVal });
    const trigger = {
      id: newVal,
      compare: "EQ",
      action: "ACTIVATE",
      targetKey: "",
      targetType: "",
      targetName: ""
    };
    triggers.push(trigger);

    const firstChoice = ObjectUtils.getDeepProperty(question, "choices", 0);
    if (firstChoice) {
      trigger.answerValue = firstChoice.value;
    }

    this.setState({ triggers: triggers });
  }

  handleRemoveTriggerOption(trigger) {
    this.setState({ triggers: ObjectUtils.removeObjectByProp(this.state.triggers, "id", trigger.id) });
  }

  handleChangeSelectedValue(trigger, event) {
    const answerValue = event.target.value;
    this.setState(({ triggers }) => ({
      triggers: triggers.map((currentTrigger) =>
        currentTrigger.id === trigger.id ? { ...currentTrigger, answerValue } : currentTrigger
      )
    }));
  }

  handleChangeSelectedTarget(trigger, event) {
    const currentTrigger = ObjectUtils.findByObjectProp(this.state.triggers, "id", trigger.id);
    let selectedTarget = ObjectUtils.findByObjectProp(this.props.invisible, "key", event.target.value);
    if (!selectedTarget.name) {
      selectedTarget = ObjectUtils.findByObjectProp(this.props.invisible, "name", event.target.value);
    }
    currentTrigger.targetKey = selectedTarget.key;
    currentTrigger.targetType = selectedTarget.triggerTargetType;
    currentTrigger.targetName = selectedTarget.name;
    this.setState({ triggers: this.state.triggers });
  }

  static getDerivedStateFromProps(props, state) {
    let { question } = props;
    if (question !== state.lastQuestion) {
      let triggers = ObjectUtils.deepClone(coalesce(ObjectUtils.getDeepProperty(question, "triggers"), []));
      let counter = QuestionTriggerPopup.calculateCounter(triggers);
      return {
        triggers,
        counter,
        lastQuestion: question
      };
    }

    // Return null to indicate no change to state.
    return null;
  }

  makeTextFit(text) {
    let title = cleanText(text);
    if (text.length > 120) {
      title = title.substring(0, 117) + "...";
    }
    return title;
  }

  getTargetIdentifier(target) {
    return coalesce(target.key, target.name);
  }

  getTriggerTargetIdentifier(trigger) {
    return coalesce(trigger.targetKey, trigger.targetName);
  }

  findTargetByTrigger(targets, trigger) {
    let target = {};
    if (trigger.targetKey) {
      target = ObjectUtils.findByObjectProp(targets, "key", trigger.targetKey);
    }
    if (!target.name && trigger.targetName) {
      target = ObjectUtils.findByObjectProp(targets, "name", trigger.targetName);
    }
    return target;
  }

  normalizeTriggers(triggers, targets) {
    return coalesce(triggers, []).map((trigger) => {
      const target = this.findTargetByTrigger(targets, trigger);
      return target.name
        ? {
            ...trigger,
            targetKey: target.key,
            targetType: target.triggerTargetType,
            targetName: target.name
          }
        : trigger;
    });
  }

  getAvailableTargets(targets, question, pageName, trigger) {
    const targetList = coalesce(targets, []);
    const selectedTrigger = coalesce(trigger, {});
    const selectedTarget = this.findTargetByTrigger(targetList, selectedTrigger);
    const selectedTargetIdentifier = this.getTriggerTargetIdentifier(selectedTrigger);
    const availableTargets = targetList.filter((element) => {
      const isCurrentSource = element.name === question.name || element.name === pageName;
      const isSelectedLegacyTarget = selectedTarget.name
        ? element.name === selectedTarget.name
        : this.getTargetIdentifier(element) === selectedTargetIdentifier;
      return !isCurrentSource && (element.visibleTriggerTarget !== true || isSelectedLegacyTarget);
    });

    const selectedTargetIsCurrentSource =
      selectedTarget.name === question.name ||
      selectedTarget.name === pageName ||
      selectedTrigger.targetName === question.name ||
      selectedTrigger.targetName === pageName;
    if (selectedTargetIdentifier && !selectedTarget.name && !selectedTargetIsCurrentSource) {
      availableTargets.push({
        name: selectedTargetIdentifier,
        title: selectedTargetIdentifier,
        key: selectedTargetIdentifier,
        triggerTargetType: "missing",
        missingTriggerTarget: true
      });
    }

    return availableTargets;
  }

  getTargetLabel(target, intl) {
    const label = target.title ? this.makeTextFit(target.title) : target.name;
    if (target.missingTriggerTarget) {
      return `${label} (${intl.formatMessage({ id: "survey.editor.trigger.target.missing" })})`;
    }
    return target.visibleTriggerTarget
      ? `${label} (${intl.formatMessage({ id: "survey.editor.trigger.target.visible" })})`
      : label;
  }

  getSelectedTarget(trigger, targets) {
    return this.findTargetByTrigger(targets, trigger);
  }

  getTargetsByType(targets, type) {
    return targets.filter((target) => {
      return target.triggerTargetType === type && !target.visibleTriggerTarget;
    });
  }

  getVisibleTargets(targets) {
    return targets.filter((target) => {
      return target.visibleTriggerTarget === true;
    });
  }

  renderTargetOptions(targets, intl) {
    return targets.map((target, i) => (
      <option
        key={`${target.triggerTargetType}-${i}`}
        value={this.getTargetIdentifier(target)}
        disabled={target.visibleTriggerTarget || target.missingTriggerTarget}
      >
        {this.getTargetLabel(target, intl)}
      </option>
    ));
  }

  renderTargetGroup(targets, type, labelId, intl) {
    const targetsByType = this.getTargetsByType(targets, type);

    return targetsByType.length > 0 ? (
      <optgroup label={intl.formatMessage({ id: labelId })}>{this.renderTargetOptions(targetsByType, intl)}</optgroup>
    ) : null;
  }

  renderVisibleTargetGroup(targets, intl) {
    const visibleTargets = this.getVisibleTargets(targets);

    return visibleTargets.length > 0 ? (
      <optgroup label={intl.formatMessage({ id: "survey.editor.trigger.target.group.visible" })}>
        {this.renderTargetOptions(visibleTargets, intl)}
      </optgroup>
    ) : null;
  }

  handleCloseModal(event) {
    const { closeModal } = this.props;
    closeModal(event);
    ModalDialogUtil.closeModalDialogBodyFix();
  }

  render() {
    const { question, show, invisible, pageName, intl } = this.props;

    return (
      <Modal size="xl" show={show} onHide={this.handleCloseModal.bind(this)} keyboard={true}>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({ id: "survey.editor.trigger.set_trigger" })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            {this.state.triggers.map(function (trigger, triggerIndex) {
              const availableTargets = this.getAvailableTargets(invisible, question, pageName, trigger);
              const selectedTarget = this.getSelectedTarget(trigger, availableTargets);
              const invalidAnswer = !isTriggerAnswerValid(question, trigger);
              const answerId = `trigger-answer-${trigger.id}`;
              return (
                <div
                  key={trigger.id}
                  className="triggers px-3 pt-3 mb-2 rounded"
                  style={triggerIndex % 2 === 0 ? { backgroundColor: "#f8f9fa" } : undefined}
                >
                  <Row className="align-items-end">
                    <Col xs={12} md={5}>
                      <FormGroup className="mb-3">
                        <FormLabel htmlFor={answerId}>
                          {intl.formatMessage({ id: "survey.editor.trigger.answer" })}
                        </FormLabel>
                        <FormSelect
                          id={answerId}
                          value={trigger.answerValue ?? ""}
                          isInvalid={invalidAnswer}
                          aria-invalid={invalidAnswer}
                          aria-describedby={invalidAnswer ? `${answerId}-error` : undefined}
                          placeholder={intl.formatMessage({ id: "app.common.Select" })}
                          onChange={this.handleChangeSelectedValue.bind(this, trigger)}
                        >
                          {invalidAnswer && (
                            <option value={trigger.answerValue ?? ""} disabled>
                              {trigger.answerValue ?? "–"}
                            </option>
                          )}
                          {(question.choices || []).map((choice, i) => (
                            <option key={i} value={choice.value}>
                              {choice.text}
                            </option>
                          ))}
                        </FormSelect>
                        {invalidAnswer && (
                          <div id={`${answerId}-error`} className="invalid-feedback" role="alert">
                            {intl.formatMessage(
                              { id: "survey.editor.validation.invalidAnswer" },
                              { value: trigger.answerValue ?? "–" }
                            )}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col xs={12} md={6}>
                      <FormGroup className="mb-3">
                        <FormLabel>
                          {intl.formatMessage({ id: "survey.editor.trigger.make_visible" })}
                          {selectedTarget.missingTriggerTarget ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`missing-trigger-target-${trigger.id}`}>
                                  {intl.formatMessage({ id: "survey.editor.trigger.warning.target_missing" })}
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faExclamationTriangle} className="ms-2 text-danger" />
                            </OverlayTrigger>
                          ) : null}
                          {selectedTarget.visibleTriggerTarget ? (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`visible-trigger-target-${trigger.id}`}>
                                  {intl.formatMessage({ id: "survey.editor.trigger.warning.target_visible" })}
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faExclamationTriangle} className="ms-2 text-warning" />
                            </OverlayTrigger>
                          ) : null}
                        </FormLabel>
                        <FormSelect
                          defaultValue={this.getTargetIdentifier(selectedTarget) || ""}
                          placeholder={intl.formatMessage({ id: "app.common.Select" })}
                          onChange={this.handleChangeSelectedTarget.bind(this, trigger)}
                        >
                          <option value="" disabled>
                            {intl.formatMessage({ id: "survey.editor.trigger.target.placeholder" })}
                          </option>
                          {this.renderTargetGroup(
                            availableTargets,
                            "page",
                            "survey.editor.trigger.target.group.pages",
                            intl
                          )}
                          {this.renderTargetGroup(
                            availableTargets,
                            "question",
                            "survey.editor.trigger.target.group.questions",
                            intl
                          )}
                          {this.renderVisibleTargetGroup(availableTargets, intl)}
                          {this.renderTargetGroup(
                            availableTargets,
                            "missing",
                            "survey.editor.trigger.target.group.missing",
                            intl
                          )}
                        </FormSelect>
                      </FormGroup>
                    </Col>
                    <Col xs={12} md={1} className="mb-3">
                      <TrashButton
                        onClick={this.handleRemoveTriggerOption.bind(this, trigger)}
                        disabled={trigger.id === 0}
                        titleId="survey.editor.tooltip.question.trigger.delete"
                      />
                    </Col>
                  </Row>
                </div>
              );
            }, this)}
            <Row>
              <Col xs={12}>
                <Button
                  onClick={this.handleAddTriggerOption.bind(this)}
                  variant={"default"}
                  title={intl.formatMessage({ id: "survey.editor.tooltip.question.trigger.add" })}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              </Col>
            </Row>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.handleCloseModal.bind(this)} variant="secondary">
            {intl.formatMessage({ id: "app.common.Close" })}
          </Button>
          <Button onClick={this.handleSetTriggers.bind(this)} variant="warning">
            {this.props.intl.formatMessage({ id: "app.common.Apply" })}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

QuestionTriggerPopup.propTypes = {
  question: PropTypes.object,
  intl: PropTypes.object,
  pageName: PropTypes.string,
  show: PropTypes.bool,
  closeModal: PropTypes.func,
  invisible: PropTypes.array,
  setTriggers: PropTypes.func
};

export default injectIntl(QuestionTriggerPopup);
